import React, { useState } from 'react';
import { Send, CheckCircle2, ArrowLeft, ArrowRight, Truck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SYMPTOMS = [
  "Won't pull vacuum",
  "Won't freeze / not cold",
  'Error code or display issue',
  'Leaking (water or oil)',
  'Loud or failing pump',
  'Product still wet after a cycle',
  'Something else',
];

const SIZES = ['Small', 'Medium', 'Large', 'Extra large', 'Semi commercial / production'];
const URGENCY = ['Still running but struggling', 'Down right now', "Been down over a week"];
const AGES = ['Under 1 year', '1 to 3 years', '3 to 5 years', '5+ years', 'Not sure'];

const _CA_REGIONS = new Set(['ab', 'bc', 'mb', 'nb', 'nl', 'ns', 'nt', 'nu', 'on', 'pe', 'qc', 'sk', 'yt', 'alberta', 'british columbia', 'manitoba', 'ontario', 'quebec', 'québec', 'saskatchewan', 'nova scotia', 'new brunswick', 'canada']);
const _US_REGIONS = new Set(['al', 'ak', 'az', 'ar', 'ca', 'co', 'ct', 'de', 'fl', 'ga', 'hi', 'id', 'il', 'in', 'ia', 'ks', 'ky', 'la', 'me', 'md', 'ma', 'mi', 'mn', 'ms', 'mo', 'mt', 'ne', 'nv', 'nh', 'nj', 'nm', 'ny', 'nc', 'nd', 'oh', 'ok', 'or', 'pa', 'ri', 'sc', 'sd', 'tn', 'tx', 'ut', 'vt', 'va', 'wa', 'wv', 'wi', 'wy', 'texas', 'california', 'florida', 'new york', 'usa', 'united states']);

const DEFAULT_SIZE = {
  residential: 'Medium',
  'small-business': 'Large',
  'semi-commercial': 'Semi commercial / production',
};

// Multi-step qualifying quote form for the machine landing pages (light theme, multi-select symptoms).
export const MachineQuoteForm = ({ slug = 'residential', tierLabel = '', testid = 'machine-quote-form', prefill = {} }) => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [f, setF] = useState(() => ({
    symptoms: prefill.symptom ? [prefill.symptom] : [],
    details: '',
    size: prefill.size || DEFAULT_SIZE[slug] || 'Medium', urgency: prefill.urgency || '', age: '', province: '', postal: '',
    name: '', phone: '', email: prefill.email || '', rush: false,
  }));
  const [labelPhase, setLabelPhase] = useState('idle'); // idle | form | sent
  const [addr, setAddr] = useState({ street: '', city: '' });
  const [labelSubmitting, setLabelSubmitting] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleSymptom = (s) => setF((p) => ({
    ...p,
    symptoms: p.symptoms.includes(s) ? p.symptoms.filter((x) => x !== s) : [...p.symptoms, s],
  }));

  const submitLabel = async () => {
    if (!addr.street.trim() || !addr.city.trim()) { toast.error('Please add your street address and city.'); return; }
    const message = [
      'PREPAID SHIPPING LABEL REQUESTED (customer pays, add shipping to invoice)',
      `Machine: ${tierLabel || slug} · Size: ${f.size}`,
      `Pickup address: ${addr.street}, ${addr.city}${f.province ? ', ' + f.province : ''}${f.postal ? ' ' + f.postal : ''}`,
    ].join('\n');
    setLabelSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/leads/contact`, {
        name: f.name,
        email: f.email || undefined,
        phone: f.phone,
        city: addr.city,
        province: f.province || '',
        postal_code: f.postal || '',
        message,
        form_source: 'prepaid-label-request',
      });
      setLabelPhase('sent');
      toast.success('Label request received, we\'ll email your prepaid label shortly.');
    } catch (err) {
      toast.error('Could not send your request. Please try again or call us.');
    } finally {
      setLabelSubmitting(false);
    }
  };

  const next = () => {
    if (step === 1 && f.symptoms.length === 0) { toast.error('Pick at least one issue to continue.'); return; }
    setStep((s) => Math.min(3, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!f.name.trim()) { toast.error('Please add your name.'); return; }
    if (!f.phone.trim() && !f.email.trim()) { toast.error('Add a phone or email so we can reach you.'); return; }
    const message = [
      f.rush && '🔥 RUSH TURNAROUND REQUESTED',
      `Machine: ${tierLabel || slug} · Size: ${f.size}`,
      `Symptoms: ${f.symptoms.join(', ')}`,
      f.details.trim() && `Details: ${f.details.trim()}`,
      f.urgency && `Status: ${f.urgency}`,
      f.age && `Machine age: ${f.age}`,
      f.province && `Location: ${f.province}`,
      f.postal && `Postal/ZIP: ${f.postal}`,
    ].filter(Boolean).join('\n');
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/leads/contact`, {
        name: f.name,
        email: f.email || undefined,
        phone: f.phone,
        province: f.province || '',
        city: '',
        postal_code: f.postal || '',
        message,
        symptoms: f.symptoms,
        form_source: `machine-${slug}`,
        rush: f.rush,
      });
      setDone(true);
      toast.success('Got it! Our rebuild team will reach out shortly.');
    } catch (err) {
      console.error('Lead submit failed', err?.response?.data || err);
      toast.error('Something went wrong. Please try again or call us.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full px-4 py-2.5 rounded-md border bg-white border-slate-300 text-[#003456] placeholder-slate-400 focus:ring-[#CE8946] focus:border-[#CE8946] text-sm focus:outline-none focus:ring-2';
  const labelCls = 'block text-xs font-medium mb-1.5 text-slate-600';

  // Rough shipping estimate from postal/province + machine size (customer ships the unit to our shop).
  const estimateShipping = () => {
    const prov = (f.province || '').trim().toLowerCase();
    const postal = (f.postal || '').trim().toUpperCase();
    const isUS = /^\d{5}/.test(postal) || _US_REGIONS.has(prov);
    const isCA = /^[A-Z]\d[A-Z]/.test(postal) || _CA_REGIONS.has(prov);
    const big = ['Large', 'Extra large', 'Semi commercial / production'].includes(f.size);
    const west = ['ab', 'alberta', 'bc', 'british columbia', 'sk', 'saskatchewan'];
    if (isUS) {
      return { cost: big ? '$220-$450 USD' : '$150-$320 USD', time: '5 to 10 business days', note: 'Cross border shipments may include customs/duties. We\'ll confirm exact costs with your quote.' };
    }
    if (isCA) {
      if (west.includes(prov) || /^T|^V|^S/.test(postal)) {
        return { cost: big ? '$120-$220' : '$80-$150', time: '2 to 4 business days', note: 'Western Canada is closest to our shop, the fastest, most affordable lane.' };
      }
      return { cost: big ? '$180-$320' : '$120-$240', time: '4 to 7 business days', note: 'We\'ll confirm the exact freight cost with your written quote.' };
    }
    return { cost: 'Confirmed with your quote', time: 'Varies by location', note: 'Add your postal/ZIP and we\'ll include a firm shipping estimate in your quote.' };
  };

  if (done) {
    const est = estimateShipping();
    return (
      <div className="rounded-2xl p-6 text-center bg-slate-50 border border-slate-200" data-testid={`${testid}-success`}>
        <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-[#CE8946]" />
        <h4 className="text-lg font-bold text-[#003456]">Request received!</h4>
        <p className="text-sm mt-1 text-slate-500">We'll review your machine details and be in touch shortly with next steps.</p>
        <div className="mt-5 text-left bg-white border border-slate-200 rounded-xl p-4" data-testid={`${testid}-shipping-estimate`}>
          <div className="flex items-center gap-2 mb-2">
            <Truck className="h-5 w-5 text-[#CE8946]" />
            <span className="text-[#003456] font-bold text-sm">Rough shipping estimate</span>
          </div>
          <div className="flex justify-between text-sm text-slate-600">
            <span>Estimated cost</span>
            <span className="font-bold text-[#003456]">{est.cost}</span>
          </div>
          <div className="flex justify-between text-sm text-slate-600 mt-1">
            <span>Transit time</span>
            <span className="font-bold text-[#003456]">{est.time}</span>
          </div>
          <p className="text-[11px] text-slate-400 mt-3 leading-snug">{est.note} Estimate only, final shipping is confirmed in your written quote.</p>
        </div>

        {/* Prepaid shipping label */}
        <div className="mt-4 text-left" data-testid={`${testid}-label-block`}>
          {labelPhase === 'idle' && (
            <>
              <button
                onClick={() => setLabelPhase('form')}
                className="w-full inline-flex items-center justify-center gap-2 border border-[#CE8946] text-[#B87333] hover:bg-[#CE8946] hover:text-[#003456] font-bold px-6 py-3 rounded-md transition-colors"
                data-testid={`${testid}-request-label`}
              >
                <Truck className="h-4 w-4" /> Request a prepaid shipping label
              </button>
              <p className="text-[11px] text-slate-400 mt-2 text-center">We email you a ready to print label. Shipping cost is added to your quote (customer pays).</p>
            </>
          )}
          {labelPhase === 'form' && (
            <div className="space-y-3 bg-white border border-slate-200 rounded-xl p-4">
              <p className="text-sm text-[#003456] font-semibold">Where should we pick up from?</p>
              <div>
                <label className={labelCls}>Street address *</label>
                <input type="text" value={addr.street} onChange={(e) => setAddr((p) => ({ ...p, street: e.target.value }))} placeholder="123 Main St" className={inputCls} data-testid={`${testid}-label-street`} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>City *</label>
                  <input type="text" value={addr.city} onChange={(e) => setAddr((p) => ({ ...p, city: e.target.value }))} placeholder="City" className={inputCls} data-testid={`${testid}-label-city`} />
                </div>
                <div>
                  <label className={labelCls}>Postal / ZIP</label>
                  <input type="text" value={f.postal} onChange={(e) => set('postal', e.target.value)} placeholder="Postal / ZIP" className={inputCls} data-testid={`${testid}-label-postal`} />
                </div>
              </div>
              <button
                onClick={submitLabel}
                disabled={labelSubmitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-6 py-3 rounded-md transition-colors disabled:opacity-60"
                data-testid={`${testid}-label-submit`}
              >
                {labelSubmitting ? 'Sending…' : (<><Truck className="h-4 w-4" /> Send me a prepaid label</>)}
              </button>
              <p className="text-[11px] text-slate-400 text-center">Shipping cost is added to your rebuild quote (customer pays).</p>
            </div>
          )}
          {labelPhase === 'sent' && (
            <div className="bg-[#CE8946]/10 border border-[#CE8946]/40 rounded-xl p-4 flex items-start gap-3" data-testid={`${testid}-label-sent`}>
              <CheckCircle2 className="h-5 w-5 text-[#CE8946] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#003456]">Label request received! We'll email your prepaid shipping label shortly, with the shipping cost added to your quote.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const Chip = ({ active, onClick, children, tid }) => (
    <button type="button" onClick={onClick} data-testid={tid}
      className={`flex items-center gap-2 text-left text-sm px-3.5 py-2.5 rounded-md border transition-colors ${active ? 'bg-[#CE8946] border-[#CE8946] text-[#003456] font-semibold' : 'bg-slate-50 border-slate-300 text-slate-700 hover:border-[#CE8946]/60'}`}>
      {children}
    </button>
  );

  return (
    <div data-testid={testid}>
      {/* progress */}
      <div className="flex items-center gap-1.5 mb-5">
        {[1, 2, 3].map((n) => (
          <div key={n} className={`h-1.5 rounded-full flex-1 transition-all ${n <= step ? 'bg-[#CE8946]' : 'bg-slate-200'}`} />
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-4" data-testid={`${testid}-step-1`}>
          <div>
            <label className={labelCls}>What's going wrong? <span className="text-slate-400 font-normal">(select all that apply)</span> *</label>
            <div className="grid grid-cols-1 gap-2">
              {SYMPTOMS.map((s) => {
                const active = f.symptoms.includes(s);
                return (
                  <Chip key={s} active={active} onClick={() => toggleSymptom(s)} tid={`${testid}-symptom-${s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 24)}`}>
                    <span className={`w-4 h-4 rounded-[4px] border flex items-center justify-center flex-shrink-0 ${active ? 'bg-[#003456] border-[#003456]' : 'border-slate-400'}`}>
                      {active && <CheckCircle2 className="h-3 w-3 text-[#CE8946]" />}
                    </span>
                    {s}
                  </Chip>
                );
              })}
            </div>
            {f.symptoms.length > 1 && (
              <p className="text-[11px] text-[#B87333] font-medium mt-2">{f.symptoms.length} issues selected — we'll diagnose all of them together.</p>
            )}
          </div>
          <div>
            <label className={labelCls}>Add any details <span className="text-slate-400 font-normal">(optional)</span></label>
            <textarea value={f.details} onChange={(e) => set('details', e.target.value)} rows={2} placeholder="Error codes, when it started, what you've already tried…" className={`${inputCls} resize-none`} data-testid={`${testid}-details`} />
          </div>
          <div>
            <label className={labelCls}>Machine size</label>
            <select value={f.size} onChange={(e) => set('size', e.target.value)} className={inputCls} data-testid={`${testid}-size`}>
              {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <button type="button" onClick={next} className="w-full inline-flex items-center justify-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-6 py-3 rounded-md transition-colors" data-testid={`${testid}-next-1`}>
            Continue <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4" data-testid={`${testid}-step-2`}>
          <div>
            <label className={labelCls}>How long has it been an issue?</label>
            <div className="grid grid-cols-1 gap-2">
              {URGENCY.map((u) => (
                <Chip key={u} active={f.urgency === u} onClick={() => set('urgency', u)} tid={`${testid}-urgency-${u.slice(0, 6).replace(/\W/g, '').toLowerCase()}`}>{u}</Chip>
              ))}
            </div>
          </div>
          <div>
            <label className={labelCls}>Roughly how old is the machine?</label>
            <select value={f.age} onChange={(e) => set('age', e.target.value)} className={inputCls} data-testid={`${testid}-age`}>
              <option value="">Select…</option>
              {AGES.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Province / state</label>
              <input type="text" value={f.province} onChange={(e) => set('province', e.target.value)} placeholder="e.g. Alberta" className={inputCls} data-testid={`${testid}-province`} />
            </div>
            <div>
              <label className={labelCls}>Postal / ZIP</label>
              <input type="text" value={f.postal} onChange={(e) => set('postal', e.target.value)} placeholder="e.g. T1A 0A1" className={inputCls} data-testid={`${testid}-postal`} />
            </div>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={back} className="inline-flex items-center justify-center gap-2 border border-slate-300 text-[#003456] font-semibold px-4 py-3 rounded-md hover:bg-slate-50 transition-colors" data-testid={`${testid}-back-2`}>
              <ArrowLeft className="h-4 w-4" />
            </button>
            <button type="button" onClick={next} className="flex-1 inline-flex items-center justify-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-6 py-3 rounded-md transition-colors" data-testid={`${testid}-next-2`}>
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3" data-testid={`${testid}-step-3`}>
          <p className="text-sm text-slate-500 -mt-1">Where should we send your free quote?</p>
          <div>
            <label className={labelCls}>Your name *</label>
            <input type="text" value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="Full name" className={inputCls} data-testid={`${testid}-name`} />
          </div>
          <div>
            <label className={labelCls}>Phone *</label>
            <input type="tel" value={f.phone} onChange={(e) => set('phone', e.target.value)} placeholder="(555) 123-4567" className={inputCls} data-testid={`${testid}-phone`} />
          </div>
          <div>
            <label className={labelCls}>Email *</label>
            <input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} placeholder="you@example.com" className={inputCls} data-testid={`${testid}-email`} />
          </div>
          <label
            className={`flex items-center gap-3 px-3.5 py-3 rounded-md border cursor-pointer transition-colors ${f.rush ? 'bg-[#CE8946]/15 border-[#CE8946]' : 'bg-slate-50 border-slate-300 hover:border-[#CE8946]/60'}`}
            data-testid={`${testid}-rush`}
          >
            <input type="checkbox" checked={f.rush} onChange={(e) => set('rush', e.target.checked)} className="h-4 w-4 accent-[#CE8946]" data-testid={`${testid}-rush-checkbox`} />
            <span className="text-sm text-[#003456] font-medium leading-snug">I need rush turnaround <span className="text-slate-500 font-normal">— flag my job as urgent</span></span>
          </label>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={back} className="inline-flex items-center justify-center gap-2 border border-slate-300 text-[#003456] font-semibold px-4 py-3 rounded-md hover:bg-slate-50 transition-colors" data-testid={`${testid}-back-3`}>
              <ArrowLeft className="h-4 w-4" />
            </button>
            <button type="button" onClick={submit} disabled={submitting} className="flex-1 inline-flex items-center justify-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-6 py-3 rounded-md transition-colors disabled:opacity-60" data-testid={`${testid}-submit`}>
            {submitting ? 'Sending…' : (<>Get My Free Quote <Send className="h-4 w-4" /></>)}
            </button>
          </div>
          <p className="text-[11px] text-slate-400 text-center pt-1">No obligation. We'll review your details and reply with next steps.</p>
        </div>
      )}
    </div>
  );
};

export default MachineQuoteForm;
